// equip-plan.ts — Janus lane I2/I3 S2 (2026-09-28): the client-truth EQUIP family's pure half.
//
// The browser plans an equip-family change from the compiled caster row it holds and commits it to
// options/game/actions/inventory_equip_truth_commit_inc.php (schema janus.inventory.equip.v1), which proves authority from the rows,
// CASes saved row snapshots and persists the browser's decision.
//
// Kinds (the legacy endpoints' rules, verbatim):
//   equip               a HAND item: free hand, else the swap — planned by possessor-compile's resolveEquipSlot / resolveEquipSwap
//                       (the C1 envelope already runs them), handed in here as {slot, stow}
//   both_hands          the single held hand item takes both hands
//   unequip_both_hands  the two-handed grip lets go to the off hand
//   swap_hands          main <-> off (either may be empty; a two-handed grip has nothing to swap)
//   don / doff          (donning lane 2026-09-29) START putting worn gear on / taking it off: no row changes yet — the canonical Donning /
//                       Doffing status lands on the actor (origin = the row) for the item's own donning / doffing time.
//   finish_don/doff      mandatory page-observed completion; whole-row slot/holder changes are posted without an AP spend.
import { ecId, ecLean, handBack, Refusal, type JanusCtx } from '../ability/context.js';
import { getStatusEffectDurationTimes } from '../ability/statuses/get-status-effect-duration-times.js';
import { activityLog, activitySelfStatus, activityWrite, emptyActivity, reviveActivityStatus, spendActivityAp, type ActivityPlan } from '../actions/activity.js';
import { apLegCost, EQUIP_RULE_CONSTANTS, type EquipRuleReaders } from './equip-rules.js';
import { readInventoryField, readInventoryRows } from '../compile/inventory-raw-readers.js';

export const INVENTORY_EQUIP_SCHEMA = 'janus.inventory.equip.v1';
export const INVENTORY_EQUIP_ENDPOINT = '/options/game/actions/inventory_equip_truth_commit_inc.php';
export type InventoryEquipKind = 'equip' | 'both_hands' | 'unequip_both_hands' | 'swap_hands' | 'don' | 'doff' | 'finish_don' | 'finish_doff';
/** The browser's duration and native status/AP writes. Time layers are informational, never server rule inputs. */
export interface TimedEquipStart { time_int: string; time_type: string; campsite: false; activity: ActivityPlan }
export interface HandSlots { main_hand: string; off_hand: string; both_hands: string }
export interface EquipRowState { inventory_id: string; possess_id: string; equipped: string; item_qty?: number }
export interface EquipPlanRow { inventory_id: string; equipped: string; possess_id?: string;
  x_pos?: number; y_pos?: number; z_pos?: number; inventory_wares?: number; inventory_deleted?: number;
  item_qty?: number; split_from?: string }
export interface InventoryEquipCommand {
  schema: typeof INVENTORY_EQUIP_SCHEMA;
  operation_id: string;
  actor_id: string;
  kind: InventoryEquipKind;
  /** The PRIMARY row: the item an equip takes, or the row a hand kind's C1 claim names (the others are stowed / re-slotted). */
  inventory_id: string;
  before: { rows: EquipRowState[]; aps: number | null };
  plan: { rows: EquipPlanRow[] };
  /** The browser's AP decision (equip-rules.ts) and combat verdict; the server CASes the verdict and the AP it spends from. */
  ap: { cost: number; in_combat: boolean };
  /** don / doff only. */
  timed?: TimedEquipStart;
  /** Page-observed mandatory finish. The status binds its actor and origin under the save locks. */
  timed_finish?: { status_id: string; status_connected_id?: string; log_expired: boolean; status_name: string; inventory_qty: number;
    boundary?: import('../statuses/gm-boundary.js').GmBoundaryVerdict;
    split_statuses?: { source_id: string; write: import('../actions/activity.js').ActivityWrite }[] };
  client_compiled?: Record<string, unknown>;
}
export type EquipPlanResult = { ok: true; rows: EquipPlanRow[] } | { ok: false; error: string; handback?: boolean };

/** move_inventory(finish_donning|finish_doffing, qty:1, ignore_donning_doffing:true).
 * Slot/stow and exact merge identity come from the ordinary equip/move planners. A split copies its persisted source at save time.
 * Finish-doffing hands the unequipped unit to the DOFFER, including a corpse's equipment. */
export function planTimedEquipFinish(kind: 'finish_don' | 'finish_doff', inventoryRow: Record<string, unknown>, actorId: string, wornSlot = '',
  move?: { slots: HandSlots; stow: string[]; splitId: string; merge: Record<string, unknown> | null }): EquipPlanResult {
  const field = (key: string): unknown => readInventoryField(inventoryRow, key);
  const id = upper(field('inventory_id'));
  const uuid = /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/;
  if (!uuid.test(id) || !uuid.test(upper(actorId))) return { ok: false, error: 'timed-finish:inventory-row-missing', handback: true };
  const qty = Number(field('item_qty'));
  if (!Number.isSafeInteger(qty) || qty < 1 || (qty !== 1 && !move)) return { ok: false, error: 'timed-finish:inventory-split', handback: true };
  if (kind === 'finish_don' && !uuid.test(upper(wornSlot))) return { ok: false, error: 'timed-finish:equipment-slot-missing', handback: true };
  const destination: EquipPlanRow = { inventory_id: id, possess_id: upper(actorId), equipped: kind === 'finish_don' ? upper(wornSlot) : '',
    x_pos: 0, y_pos: 0, z_pos: 0, inventory_deleted: 0, inventory_wares: kind === 'finish_don' || upper(field('possess_id')) === upper(actorId) ? Number(field('inventory_wares') ?? 0) : 0 };
  let rows: EquipPlanRow[] = [destination];
  if (kind === 'finish_doff' && move?.merge) {
    const mergeId = upper(readInventoryField(move.merge, 'inventory_id')), count = Number(readInventoryField(move.merge, 'item_qty'));
    if (!uuid.test(mergeId) || !Number.isSafeInteger(count) || count < 1) return { ok: false, error: 'timed-finish:inventory-merge', handback: true };
    rows = [{ inventory_id: id, equipped: qty > 1 ? upper(field('equipped')) : '', item_qty: qty - 1,
      ...(qty === 1 ? { inventory_deleted: 1, ...(upper(field('possess_id')) !== upper(actorId) ? { inventory_wares: 0 } : {}) } : {}) },
      { inventory_id: mergeId, equipped: '', item_qty: count + 1, inventory_deleted: 0 }];
  } else if (qty > 1) {
    if (!uuid.test(upper(move!.splitId))) return { ok: false, error: 'timed-finish:inventory-split', handback: true };
    rows = [{ inventory_id: id, equipped: upper(field('equipped')), item_qty: qty - 1 },
      { ...destination, inventory_id: upper(move!.splitId), item_qty: 1, split_from: id }];
  }
  if (kind === 'finish_don' && move) {
    const equipped = planEquip(rows[rows.length - 1]!.inventory_id, wornSlot, move.stow, move.slots);
    if (!equipped.ok) return equipped;
    rows.push(...equipped.rows.slice(1));
  }
  return { ok: true, rows };
}

type Row = Record<string, unknown>;
const asRow = (v: unknown): Row | null => v !== null && typeof v === 'object' && !Array.isArray(v) ? v as Row : null;
const upper = (v: unknown): string => String(v ?? '').trim().toUpperCase();
const children = (v: unknown): unknown[] => Array.isArray(v) ? v : asRow(v) ? Object.values(asRow(v)!) : [];

/** Project one carried unit directly onto its actor for both instant and timed equip planning. The source tree stays intact for CAS/LOSS. */
export function projectEquipItemHeld(caster: Row, inventoryId: string, actorId: string): Row | null {
  let lifted: Row | null = null;
  const lift = (rows: Row[]): Row[] => rows.flatMap(row => {
    if (upper(readInventoryField(row, 'inventory_id')) === upper(inventoryId)) { lifted = row; return []; }
    const contents = readInventoryRows(row, 'inventory_in_container');
    return contents.length ? [{ ...row, inventory_in_container: lift(contents) }] : [row];
  });
  const inventory = lift(readInventoryRows(caster, 'inventory'));
  if (!lifted) return null;
  const item = lifted as Row;
  return { ...caster, inventory: [...inventory, { ...item, inventory: { ...asRow(item.inventory), possess_id: upper(actorId), item_qty: 1 } }] };
}

/** Every row of a compiled caster's inventory tree: id, holder, slot, quantity, depth (top-level first when sorted by depth).
 *  Shapeshift-ABSORBED gear (inventory_private 2) is not carried — its slot is restoration provenance (calculate_equipped_counts). */
export function carriedRows(caster: Row): (EquipRowState & { depth: number; qty: number })[] {
  const out: (EquipRowState & { depth: number; qty: number })[] = [];
  const seen = new Set<string>();
  const walk = (list: unknown, depth: number): void => {
    if (depth > 6) return;
    for (const node of children(list)) {
      const r = asRow(node);
      const bucket = asRow(r?.inventory);
      const id = upper(bucket?.inventory_id);
      if (!r || !bucket || !id || seen.has(id) || Number(bucket.inventory_private) === 2) continue;
      seen.add(id);
      const qty = Number(bucket.item_qty ?? 1);
      out.push({ inventory_id: id, possess_id: upper(bucket.possess_id), equipped: upper(bucket.equipped), depth, qty: Number.isFinite(qty) ? qty : 1 });
      walk(r.inventory_in_container, depth + 1);
    }
  };
  walk(caster.inventory, 0);
  return out;
}

/** First occupant per hand slot (top-level first, then row order) + the compiled counts' meaning (both hands occupy both). */
export function handState(rows: (EquipRowState & { depth: number })[], slots: HandSlots): { main: string; off: string; both: string; mainBusy: boolean; offBusy: boolean } {
  const ordered = [...rows].sort((a, b) => a.depth - b.depth);
  const first = (slot: string): string => ordered.find(r => r.equipped !== '' && r.equipped === upper(slot))?.inventory_id ?? '';
  const main = first(slots.main_hand), off = first(slots.off_hand), both = first(slots.both_hands);
  return { main, off, both, mainBusy: main !== '' || both !== '', offBusy: off !== '' || both !== '' };
}

/** The hand kinds' plan (inventory_equip_core_inc.php ft_equip_plan twin). */
export function planHands(kind: 'both_hands' | 'unequip_both_hands' | 'swap_hands', caster: Row, slots: HandSlots): EquipPlanResult {
  const h = handState(carriedRows(caster), slots);
  if (kind === 'both_hands') {
    if (h.both !== '' || (h.main !== '' && h.off !== '')) return { ok: false, error: 'One hand must be free to equip into both hands.' };
    const id = h.main !== '' ? h.main : h.off;
    if (id === '') return { ok: false, error: 'No Main or off hand to equip into both hands!' };
    return { ok: true, rows: [{ inventory_id: id, equipped: upper(slots.both_hands) }] };
  }
  if (kind === 'unequip_both_hands') {
    if (h.both === '') return { ok: false, error: 'Nothing is held in both hands.' };
    return { ok: true, rows: [{ inventory_id: h.both, equipped: upper(slots.off_hand) }] };
  }
  if (h.both !== '') return { ok: false, error: 'A two-handed grip has nothing to swap.' };
  if (h.main === '' && h.off === '') return { ok: false, error: 'No items in hands to swap!' };
  const rows: EquipPlanRow[] = [];
  if (h.main !== '') rows.push({ inventory_id: h.main, equipped: upper(slots.off_hand) });
  if (h.off !== '') rows.push({ inventory_id: h.off, equipped: upper(slots.main_hand) });
  return { ok: true, rows };
}

/** The envelope's `equip` covers a single, directly-held hand item: a stack (move_inventory splits it) or a row in a bag / sheath hands
 *  back to the legacy route (the server's rule, inventory_equip_core_inc.php). */
export function equipItemEligible(caster: Row, inventoryId: string, actorId: string): boolean {
  const row = carriedRows(caster).find(r => r.inventory_id === upper(inventoryId));
  return !!row && row.qty === 1 && row.possess_id === upper(actorId) && row.equipped === '';
}

/** An equip's plan from the slot / stow the C1 planner resolved: the item takes `slot`, each stowed row lets go. A worn slot (armor,
 *  clothing, a ring…) is planned the same way (donning lane 2026-09-29: gear WITHOUT a donning time equips at once; one WITH a donning time
 *  is a `don` start instead — planTimedEquipStart). Only a hand item ever stows. */
export function planEquip(inventoryId: string, slot: string, stow: string[], slots: HandSlots): EquipPlanResult {
  if (!/^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/.test(upper(slot))) return { ok: false, error: 'handback', handback: true };
  const hand = [slots.main_hand, slots.off_hand, slots.both_hands].map(upper);
  if (!hand.includes(upper(slot)) && stow.length) return { ok: false, error: 'handback', handback: true };
  return { ok: true, rows: [{ inventory_id: upper(inventoryId), equipped: upper(slot) }, ...[...new Set(stow.map(upper))].filter(s => s && s !== upper(inventoryId)).map(s => ({ inventory_id: s, equipped: '' }))] };
}

/** The creature's TOP-LEVEL rows in `slot` (count_equipped_items() — $possessor['inventory'] only, never a bag's contents). */
export function equippedInSlot(caster: Row, slot: string): number {
  return carriedRows(caster).filter(r => r.depth === 0 && r.equipped !== '' && r.equipped === upper(slot)).length;
}

/** The time layers a timed start reads — move_inventory's apply_donning_doffing_status_and_exit(): `<don|doff>fing_time_int` / `_type`
 *  as layer_value() answers them (a missing type reads ''). `value` is the page's layer reader on the FAT row. Null = no timer (the move is
 *  instant: needs_donning_or_doffing() reads the int with !empty()). */
export function timedStartOf(kind: 'don' | 'doff', value: (key: string) => unknown): Pick<TimedEquipStart, 'time_int' | 'time_type'> | null {
  const prefix = kind === 'don' ? 'donning' : 'doffing';
  const scalar = (v: unknown): unknown => Array.isArray(v) ? v[0] : v;
  const int = scalar(value(`${prefix}_time_int`));
  if (int === undefined || int === null || int === false || int === 0 || int === '' || int === '0') return null;
  const type = scalar(value(`${prefix}_time_type`));
  return { time_int: String(int), time_type: type === undefined || type === null ? '' : String(type) };
}

/** A creature already holding this timed status (apply_status_effect() will not apply a second one: the start would change nothing). */
export function holdsStatus(caster: Row, statusItemId: string): boolean {
  const statuses = asRow(caster.statuses);
  // A status row's id sits at status_item_id on a compiled row, and under status_arr.items on the lean row a caster delta delivers.
  const idOf = (s: Row | null): string => upper(s?.status_item_id ?? asRow(asRow(s?.status_arr)?.items)?.item_id ?? asRow(s?.items)?.item_id);
  return [...children(statuses?.target)].some(s => idOf(asRow(s)) === upper(statusItemId) && Number(asRow(s)?.status_deleted ?? 0) === 0);
}

/** START only: the inventory stays put until the existing page-observed finish. The actor pays even when doffing a body's gear. */
export function planTimedEquipStart(ctx: JanusCtx, actor: Row, item: Row, kind: 'don' | 'doff', read: EquipRuleReaders, prior?: Row | null, apCredit = 0): ActivityPlan {
  const status = kind === 'don' ? EQUIP_RULE_CONSTANTS.donning : EQUIP_RULE_CONSTANTS.doffing;
  if (holdsStatus(actor, status)) throw new Refusal(`You are already ${kind === 'don' ? 'donning' : 'doffing'} something — finish that first.`);
  const origin = upper(read.inventoryField(item, 'inventory_id'));
  if (!origin || !timedStartOf(kind, key => read.value(item, key))) handBack('inventory-equip:timer-not-held');
  const plan = emptyActivity();
  if (ctx.read.inCombat(ctx.encounter, actor)) {
    const cost = apLegCost(actor, item, kind === 'don' ? 'equip' : 'stow', read);
    if (cost === null) handBack('inventory-equip:ap-not-held');
    spendActivityAp(plan, ctx, actor, Math.max(0, cost - Math.max(0, Math.trunc(apCredit))));
  }
  const prefix = kind === 'don' ? 'donning' : 'doffing';
  const duration = { item_layer_types: { [`${prefix}_time_int`]: read.value(item, `${prefix}_time_int`), [`${prefix}_time_type`]: read.value(item, `${prefix}_time_type`) } };
  const ends = getStatusEffectDurationTimes(ctx, duration, actor).ends;
  activityWrite(plan, reviveActivityStatus(activitySelfStatus(ctx, actor, status, origin, ends), prior));
  activityLog(plan, actor, 'status_effect_applied', status, ecId(actor));
  return plan;
}

/** The `before` the server CASes: every hand-slot row the caster holds + the item (when named), and its AP. */
export function equipBefore(caster: Row, slots: HandSlots, inventoryId = ''): { rows: EquipRowState[]; aps: number | null } {
  const hand = new Set([slots.main_hand, slots.off_hand, slots.both_hands].map(upper));
  const rows = carriedRows(caster).filter(r => hand.has(r.equipped) || (inventoryId !== '' && r.inventory_id === upper(inventoryId)))
    .map(({ inventory_id, possess_id, equipped }) => ({ inventory_id, possess_id, equipped }))
    .sort((a, b) => a.inventory_id.localeCompare(b.inventory_id));
  const heldAps = ecLean(caster, 'aps');
  const aps = heldAps == null ? NaN : Number(heldAps);
  return { rows, aps: Number.isFinite(aps) ? Math.trunc(aps) : null };
}

/** Patch slots/holders in compiled copies; a finish projection also applies the complete saved row. Returns the hits. */
export function applyEquippedToTree(node: unknown, rows: EquipPlanRow[], options: { completeRows?: boolean } = {}, depth = 0): number {
  if (depth > 24 || rows.length === 0) return 0;
  const want = new Map(rows.map(r => [upper(r.inventory_id), r]));
  let hits = 0;
  if (Array.isArray(node)) { for (const child of node) hits += applyEquippedToTree(child, rows, options, depth + 1); return hits; }
  const obj = asRow(node);
  if (!obj) return 0;
  const bucket = asRow(obj.inventory);
  const planned = bucket ? want.get(upper(readInventoryField(obj, 'inventory_id'))) : undefined;
  if (bucket && planned) {
    if (options.completeRows) {
      const { inventory_id, split_from, ...changes } = planned;
      Object.assign(bucket, changes);
    }
    const slot = upper(planned.equipped);
    if (slot === '') delete bucket.equipped; else bucket.equipped = slot;
    if (planned.possess_id) bucket.possess_id = upper(planned.possess_id);
    hits++;
  }
  for (const [key, child] of Object.entries(obj)) if (!(key === 'inventory' && !Array.isArray(child))) hits += applyEquippedToTree(child, rows, options, depth + 1);
  return hits;
}
